import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// 导入组件
import LoginPage from './pages/Login';
import LandingPage from './pages/Landing';
import TransPage from './pages/Trans';
import ServicesPage from './pages/Services';
import CertificationPage from './pages/Certification';
import PricingPage from './pages/Pricing';
import SecurityPage from './pages/Security';
import ProfilePage from './pages/Profile';
import PrivacyPage from './pages/Privacy';
import TermsPage from './pages/Terms';
import PaymentRefundPage from './pages/PaymentRefund';
import GuidePage from './pages/Guide';
import DatePickerDemo from './pages/DatePickerDemo';
import AppLayout from './components/Layout/AppLayout';
import AccountDisabledPage from './components/Common/AccountDisabledPage';
import { checkAuthStatus } from './store/actions/authActions';
import { showNotification } from './store/actions/uiActions';
import { setGlobalNotificationHandler } from './services/api';

// 导入自定义主题和全局样式
import theme from './assets/theme';
import './assets/globalStyles.css';

// 需要认证的路由组件（仅用于个人中心等特殊页面）
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, authChecked } = useSelector((state) => state.auth);
  
  if (!authChecked) {
    return <div>加载中...</div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/landing" />;
};

function App() {
  const dispatch = useDispatch();
  
  useEffect(() => {
    // 设置全局通知处理器
    setGlobalNotificationHandler((message) => {
      dispatch(showNotification(message, 'error'));
    });
    
    // 添加全局未捕获 Promise rejection 处理器
    const handleUnhandledRejection = (event) => {
      console.warn('🚨 [App] 捕获到未处理的 Promise rejection:', event.reason);
      
      // 检查是否是我们已经处理过的错误
      if (event.reason && event.reason.handled) {
        console.log('✅ [App] 这是已处理的错误，忽略');
        event.preventDefault(); // 阻止浏览器显示错误
        return;
      }
      
      // 对于真正未处理的错误，记录但不显示给用户
      console.error('❌ [App] 未处理的错误:', event.reason);
      event.preventDefault(); // 阻止浏览器显示错误
    };
    
    // 添加事件监听器
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    dispatch(checkAuthStatus());
    
    // 清理函数
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [dispatch]);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        
        {/* 公开页面 - 隐私协议和用户协议 */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/payment-refund" element={<PaymentRefundPage />} />
        <Route path="/guide" element={<GuidePage />} />
        
        {/* 账号禁用页面 */}
        <Route path="/account-disabled" element={<AccountDisabledPage />} />
        
        {/* Landing页面 - 公开访问 */}
        <Route path="/landing" element={<LandingPage />} />
        
        {/* 其他页面 - 公开访问，但某些功能需要登录 */}
        <Route path="/trans" element={
          <AppLayout>
            <TransPage />
          </AppLayout>
        } />
        
        <Route path="/services" element={
          <AppLayout>
            <ServicesPage />
          </AppLayout>
        } />
        
        <Route path="/certification" element={
          <AppLayout>
            <CertificationPage />
          </AppLayout>
        } />
        
        <Route path="/pricing" element={
          <AppLayout>
            <PricingPage />
          </AppLayout>
        } />
        
        <Route path="/security" element={
          <AppLayout>
            <SecurityPage />
          </AppLayout>
        } />
        
        {/* 个人中心 - 需要认证 */}
        <Route path="/profile" element={
          <PrivateRoute>
            <AppLayout>
              <ProfilePage />
            </AppLayout>
          </PrivateRoute>
        } />
        
        {/* 保留原有的演示页面 */}
        <Route path="/date-picker-demo" element={
          <AppLayout>
            <DatePickerDemo />
          </AppLayout>
        } />
        
        {/* 默认重定向到landing页面 */}
        <Route path="*" element={<Navigate to="/landing" replace />} />
      </Routes>
    </ThemeProvider>
  );
}

export default App;